import { computed, nextTick, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import requirementTypesApi from '@/api/requirementTypesApi'
import { EventBus } from '@/event-bus/event-bus'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import { DataFieldType } from '@/utils/constants'

const useDomainModel = () => {
  const loadingDeleteModel = ref(false)

  const store = useStore()
  const route = useRoute()
  const workflow = computed(() => store.state.workflow.workflow)
  const svgGraph = computed(() => safeStructuredClone(store.getters['svgGraphData/svgGraphData']))
  const svgLanes = computed(() => safeStructuredClone(store.getters['svgGraphData/svgLanesData']))
  const showEntities = computed(() => store.state.domainModelShowEntities)

  const validateFieldName = (event, newValue, originalValue) => {
    const maxLength = 30
    if (newValue.length > maxLength) {
      alert(`Data field name must be a maximum of ${maxLength} characters.`)
      diagramHelpers.methods.restoreValue(event, originalValue)
      return false
    }
    return true
  }

  const cardTypeColor = (cardType) => {
    return requirementTypesApi.getBackgroundColour(cardType)
  }

  const getDataModelFieldsFromEvent = (event, cardId) => {
    const dataModelIndex = event.dataModels?.findIndex((model) => model.cardId === cardId)
    const dataModel = event.dataModels?.[dataModelIndex]

    return { dataFields: dataModel?.dataFields || [], originalIndex: dataModelIndex ?? -1 }
  }

  const getCardHeight = (event, card, baseHeight = 0) => {
    const isQuery = !!card?.cardType?.queryType

    const cardData = getDataModelFieldsFromEvent(event, card.id)
    const { originalIndex, dataFields } = cardData || {}

    if (!showEntities.value) {
      return `${(isQuery ? (originalIndex === -1 ? 92 : 176) + (!card.isInContext ? 16 : 0) : 64)}px`
    }

    const dataFieldsCount = dataFields.reduce((acc, cur) => {
      if (cur.dataFields?.length) {
        acc += cur.dataFields.length
      }

      return ++acc
    }, 0)

    const hasRelatedEntity = dataFields.some((field) => field.relatedEntityId)

    // 74 - top/bottom borders (3px) + padding (24px) + input height (31px) + toolbar height (16px)
    // 16 - data fields wrapper padding py-2 (16px)
    // 60 for query - cardinality (26px) + margin under combobox (4px) + space for arrows or add more (32px)
    // 16 for not in bounded context queries (top border has extra 16px)
    const initialHeight = 74 + (dataFieldsCount ? 16 : 0) + (isQuery ? 60 : 0) + (isQuery && !card.isInContext ? 16 : 0)

    return originalIndex !== -1
      ? `${initialHeight + (hasRelatedEntity ? 16 : 0) + (dataFieldsCount * 25) + baseHeight}px`
      : `${(isQuery ? 110 : 82) + (isQuery && !card.isInContext ? 16 : 0)}px`
  }

  const removeCardAndModel = async (event, card) => {
    loadingDeleteModel.value = true
    await workflowApi.deleteCardAndModel(event, card, svgGraph.value, workflow.value)
    loadingDeleteModel.value = false
  }

  const getRelatedEntity = (field) => {
    return workflow.value.dataModels?.find((entity) => entity.id === field.relatedEntityId)
  }

  const getMatchingFieldInEntity = (entity, dataField) => {
    if (!entity) {
      return null
    }

    const entityDataFields = entity.dataFields || []
    const matchingField = entityDataFields.find((field) => field.name === dataField)
    let relatedEntity

    if (matchingField && matchingField.relatedEntityId) {
      relatedEntity = (workflow.value.dataModels ?? []).find((item) => item.id === matchingField.relatedEntityId)
    }

    return matchingField ? { ...matchingField, relatedEntity } : null
  }

  const isNameNotInEntity = (entityId, fieldName) => {
    if (!entityId || !workflow.value.dataModels) {
      return false
    }

    const entity = workflow.value.dataModels.find((entity) => entity.id === entityId) ?? { dataFields: [] }
    const fieldNames = entity.dataFields.map((field) => field.name)
    return !fieldNames.includes(fieldName)
  }

  const addSubField = (event, cardId, index) => {
    const modelIndex = (event.dataModels ?? []).findIndex((dataModel) => dataModel.cardId === cardId)

    if (modelIndex === -1) {
      return
    }

    const addColumn = (event) => {
      const updatedFields = event.dataModels[modelIndex].dataFields[index].dataFields ?? []
      updatedFields.push({
        name: 'NEW SUB FIELD ' + (updatedFields.length + 1),
        exampleData: ['']
      })
      event.dataModels[modelIndex].dataFields[index].dataFields = updatedFields
    }
    EventBus.$emit('update-data-model-with-function', {
      updateFunction: addColumn,
      eventId: event.id || route.params.eventId,
      skipFocus: true
    })
  }

  const blurDataField = async (event, workflowEvent, indexDataModel, indexDataField) => {
    const dataModel = svgGraph.value[workflowEvent.id]?.dataModels[indexDataModel]

    if (!dataModel || indexDataField === -1 || indexDataField === undefined) {
      return
    }

    const fieldName = 'name'
    const newName = (event.target.innerText || '').trim()
    const oldName = dataModel.dataFields[indexDataField][fieldName]

    if (newName === oldName || !validateFieldName(event, newName, oldName)) {
      return
    }

    dataModel.dataFields[indexDataField][fieldName] = newName

    // Wait for possible other blur events to complete
    await nextTick()

    await workflowApi.updateWorkflow({
      svgGraph: svgGraph.value,
      svgLanes: svgLanes.value,
      workflow: workflow.value,
      updateNote: {
        action: 'updated data model',
        target: svgGraph.value[workflowEvent.id].name
      }
    }, true)
  }

  const blurSubDataField = async (event, workflowEvent, cardId, indexDataField, subIndexField) => {
    const indexDataModel = svgGraph.value[workflowEvent.id]?.dataModels.findIndex((dataModel) => dataModel.cardId === cardId)
    const dataModel = svgGraph.value[workflowEvent.id]?.dataModels[indexDataModel]

    if (!dataModel || indexDataField === -1 || indexDataField === undefined) {
      return
    }

    const fieldName = 'name'
    const newName = (event.target.innerText || '').trim()
    const oldName = dataModel.dataFields[indexDataField].dataFields[subIndexField][fieldName]

    if (newName === oldName || !validateFieldName(event, newName, oldName)) {
      return
    }

    dataModel.dataFields[indexDataField].dataFields[subIndexField][fieldName] = newName

    // Wait for possible other blur events to complete
    await nextTick()

    await workflowApi.updateWorkflow({
      svgGraph: svgGraph.value,
      svgLanes: svgLanes.value,
      workflow: workflow.value,
      updateNote: {
        action: 'updated data model',
        target: svgGraph.value[workflowEvent.id].name
      }
    }, true)
  }

  const removeSubDataField = async (event, cardId, index, subIndex) => {
    const updatedDataModels = safeStructuredClone(event.dataModels)

    const modelIndex = event.dataModels.findIndex((dataModel) => dataModel.cardId === cardId)

    if (modelIndex > -1) {
      updatedDataModels[modelIndex].dataFields[index].dataFields.splice(subIndex, 1)
      await workflowApi.updateDataModels({
        eventId: event.id,
        dataModelsOnEvent: updatedDataModels,
        workflow: workflow.value
      })
    }
  }

  const onSelectRelatedEntity = (event, cardId, fieldIndex, entity) => {
    const modelIndex = event.dataModels.findIndex((dataModel) => dataModel.cardId === cardId)

    if (modelIndex === -1) {
      return
    }

    const selectEntity = (event) => {
      event.dataModels[modelIndex].dataFields[fieldIndex].relatedEntityId = entity?.id || null
    }
    EventBus.$emit('update-data-model-with-function', {
      updateFunction: selectEntity,
      eventId: event.id || route.params.eventId
    })
  }

  const addDataField = (eventId, cardId, isCommand) => {
    if (!eventId && !route.params.eventId) {
      return
    }

    const addColumn = (event) => {
      const dataModelIndex = (event.dataModels ?? []).findIndex((dataModel) => dataModel.cardId === cardId)

      if (dataModelIndex === -1) {
        event.dataModels = [...(event.dataModels || []), {
          name: isCommand ? event.name + ' by ' + getLaneNameFromId(event.laneId) : 'New Read Model',
          type: isCommand ? 'Event Story' : 'Read Model',
          dataFields: [
            {
              name: 'NEW FIELD 1',
              exampleData: ['Example 1']
            }
          ],
          cardId
        }]
      } else {
        event.dataModels[dataModelIndex].dataFields.splice(event.dataModels[dataModelIndex].dataFields.length, 0, {
          name: 'NEW FIELD ' + (event.dataModels[dataModelIndex].dataFields.length + 1),
          exampleData: ['']
        })
      }
    }
    EventBus.$emit('update-data-model-with-function', {
      updateFunction: addColumn,
      eventId: eventId || route.params.eventId,
      skipFocus: true
    })
  }

  const removeDataField = async (event, cardId, index) => {
    const updatedDataModels = safeStructuredClone(event.dataModels)

    const modelIndex = event.dataModels.findIndex((dataModel) => dataModel.cardId === cardId)

    if (modelIndex > -1) {
      if (updatedDataModels[modelIndex].dataFields.length === 1) {
        updatedDataModels.splice(modelIndex, 1)
      } else {
        updatedDataModels[modelIndex].dataFields.splice(index, 1)
      }
      await workflowApi.updateDataModels({
        eventId: event.id,
        dataModelsOnEvent: updatedDataModels,
        workflow: workflow.value
      })
    }
  }

  const mergeDataFields = (_dataFields, _fields) => {
    const dataFields = JSON.parse(JSON.stringify(_dataFields))
    const fields = JSON.parse(JSON.stringify(_fields))

    for (const field of fields) {
      const dataFieldNames = dataFields.map(df => df.name)
      if (field.relatedEntityId && field.checked === undefined) {
        const index = dataFieldNames.indexOf(field.name)
        const subDataFields = (index !== -1 ? dataFields[index].dataFields : []) ?? []
        const subFieldNames = subDataFields.map(df => df.name)
        let isSubChecked = false
        for (const subField of field.entityFields) {
          isSubChecked = isSubChecked || subField.checked
          if (subField.checked && !subFieldNames.includes(subField.name)) {
            delete subField.checked
            subDataFields.push(JSON.parse(JSON.stringify(subField)))
          }
          if (!subField.checked && subFieldNames.includes(subField.name)) {
            const index = subDataFields.findIndex(df => df.name === subField.name)
            if (index !== -1) subDataFields.splice(index, 1)
          }
        }
        delete field.entityFields
        delete field.cardinality
        field.dataFields = subDataFields
        const clonedField = JSON.parse(JSON.stringify(field))
        if (isSubChecked) {
          if (index === -1) dataFields.push(clonedField)
          else dataFields[index] = clonedField
        } else {
          if (index !== -1) dataFields.splice(index, 1)
        }
      } else {
        delete field.entityFields
        if (field.checked && !dataFieldNames.includes(field.name)) {
          delete field.checked
          dataFields.push(JSON.parse(JSON.stringify(field)))
        }
        if (!field.checked && dataFieldNames.includes(field.name)) {
          const index = dataFields.findIndex(df => df.name === field.name)
          if (index !== -1) dataFields.splice(index, 1)
        }
      }
    }

    return dataFields
  }

  const updateDataFields = async (eventId, cardId, isCommand, fields) => {
    if (!eventId && !route.params.eventId) {
      return
    }

    const updateFields = (event) => {
      const dataModelIndex = (event.dataModels || []).findIndex((dataModel) => dataModel.cardId === cardId)

      if (dataModelIndex === -1) {
        event.dataModels = [...(event.dataModels || []), {
          name: isCommand ? event.name + ' by ' + getLaneNameFromId(event.laneId) : 'New Read Model',
          type: isCommand ? 'Event Story' : 'Read Model',
          dataFields: mergeDataFields([], fields),
          cardId
        }]
      } else {
        event.dataModels[dataModelIndex].dataFields = mergeDataFields(event.dataModels[dataModelIndex].dataFields, fields)
      }
    }

    EventBus.$emit('update-data-model-with-function', {
      updateFunction: updateFields,
      eventId: eventId || route.params.eventId,
      skipFocus: true
    })
  }

  const toggleFieldFilter = async (event, cardId, fieldIndex, subFieldIndex) => {
    const { dataModels, id: eventId } = event
    const modelIndex = dataModels.findIndex(model => model.cardId === cardId)
    if (modelIndex === -1) return

    const updatedDataModels = safeStructuredClone(dataModels)
    const field = subFieldIndex === undefined
      ? updatedDataModels[modelIndex].dataFields[fieldIndex]
      : updatedDataModels[modelIndex].dataFields[fieldIndex].dataFields[subFieldIndex]

    field.type = field.type === DataFieldType.FILTER
      ? DataFieldType.NORMAL
      : DataFieldType.FILTER

    await workflowApi.updateDataModels({
      eventId,
      dataModelsOnEvent: updatedDataModels,
      workflow: workflow.value
    })
  }

  const hideDataField = (event, cardId, fieldIndex) => {
    const modelIndex = event.dataModels.findIndex((dataModel) =>
      cardId ? dataModel.cardId === cardId : dataModel.type === 'Event Story')
    if (modelIndex === -1) return

    const hideField = (event) => {
      const field = event.dataModels[modelIndex].dataFields[fieldIndex]
      field.hideInForm = !field.hideInForm
    }

    EventBus.$emit('update-data-model-with-function', {
      updateFunction: hideField,
      eventId: event.id || route.params.eventId,
      skipFocus: true
    })
  }

  const getLaneNameFromId = (laneId) => {
    let res = ''
    const index = workflow.value.lanes.findIndex((lane) => lane.id === laneId)
    if (index >= 0) {
      res = workflow.value.lanes[index].name
    }
    return res
  }

  const createCard = async (eventId, cardTypeId, text) => {
    await workflowApi.createRequirementJson(route.params.id, eventId, {
      cardTypeId,
      description: text,
      cardinality: 'one-to-many'
    })
  }

  return {
    validateFieldName,
    cardTypeColor,
    getCardHeight,
    getDataModelFieldsFromEvent,
    removeCardAndModel,
    getRelatedEntity,
    isNameNotInEntity,
    addSubField,
    blurDataField,
    blurSubDataField,
    removeSubDataField,
    onSelectRelatedEntity,
    removeDataField,
    addDataField,
    mergeDataFields,
    updateDataFields,
    hideDataField,
    getMatchingFieldInEntity,
    toggleFieldFilter,
    createCard,
    loadingDeleteModel
  }
}

export default useDomainModel
